import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { FirSysFormChartAcctMapService } from './fir-sys-form-chart-acct-map.service';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-fir-sys-form-chart-acct-map',
    templateUrl: './fir-sys-form-chart-acct-map.component.html',
    styleUrls: ['./fir-sys-form-chart-acct-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class FirSysFormChartAcctMapComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private isInitialized = false;
  private unsubscribe$ = new Subject<void>();
  private readonly contextSubs = new Subscription();
  /** Bumped on each `init()` / destroy so slower HTTP callbacks cannot commit after a newer context. */
  private loadEpoch = 0;
  /** Serial for `fetchSystemAcctFormData` (dropdown / save refresh can overlap). */
  private formFetchEpoch = 0;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};
  filterParams: any = {};
  rowData: any = {};

  minimise = false;
  crudLoading = false;
  showFormLoader = true;

  loadingFirSysAcct = true;
  loadingFirLookup = true;
  loadingIRSform = false;

  firSystemAcctDetails: any = {};
  fir_sys_acct_selected = '';
  fir_sys_acc_dropdown_data: any[] = [];
  fir_sys_acc_dropdown_data_TEMP: any[] = [];

  firIRSFormData: any[] = [];
  firSystemAcctLookupTemp: any[] = [];
  firLookupcheckdata: any[] = [];

  firLookupForm: string[] = [];
  firLookupSchedule: string[] = [];
  firLookupLine: string[] = [];
  firLookupColumn: string[] = [];
  firLookupSysMapAcct: string[] = [];

  CHECK_FIR_FORM = '';
  CHECK_FIR_SCHEDULE = '';
  CHECK_FIR_LINE = '';
  CHECK_FIR_COL = '';
  CHECK_FIR_SYS_MAP_ACCT = '';

  showFirDetailsText = 'lookup';
  firLookUpMapping = false;
  old_fir_form_acct_key: any = null;
  old_fir_form_map_type = '';
  currentEditedIndex = -1;

  orderByField = 'FORM';
  reverseSort = false;
  sort_key: Record<string, number> = { '1': 0, '2': 0, '3': 0, '4': 0, '5': 0 };

  private firLookupData1: any[] = [];
  private firLookupData2: any[] = [];
  private firLookupData3: any[] = [];

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private firSysFormService: FirSysFormChartAcctMapService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.loadEpoch++;
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.loadEpoch++;
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
        this.cdr.markForCheck();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG || {};
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.loadEpoch++;
    this.formFetchEpoch++;
    this.contextSubs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private tryInit() {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isInitialized = true;
    this.init();
  }

  private init() {
    const epoch = ++this.loadEpoch;
    try {
      this.firSystemAcctDetails = {
        acct_type: this.rowData.ACCT_TYPE,
        system_acct: this.rowData.SYSTEM_ACCT,
        system_acct_desc: this.rowData.SYSTEM_ACCT_DESC
      };
      this.showFormLoader = false;
      this.getFirSystemAcctdropdown(epoch);
      this.fetchFirSystemAcctLookup(epoch);
    } catch (e) {
      console.error('Error in init:', e);
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  private getFirSystemAcctdropdown(epoch: number) {
    const params = {
      action_code: 'pfwnw7',
      tax_year: this.rowData.TAX_YEAR ?? this.globalService?.globalParams?.tax_year,
      scenario: this.rowData.SCENARIO ?? this.globalService?.globalParams?.scenario,
      jcd_key: this.rowData.JCD_KEY ?? this.globalService?.globalParams?.jcd_key
    };

    this.firSysFormService.loadJsonObject(this.GENERAL_CONFIG.base_url, params).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (epoch !== this.loadEpoch) return;
        if (data.callSuccess === '1') {
          this.fir_sys_acc_dropdown_data = [];
          for (const idx in data.jsonObject) {
            this.fir_sys_acc_dropdown_data.push(data.jsonObject[idx].SYS_DROPDOWN);
          }
          this.fir_sys_acc_dropdown_data_TEMP = data.jsonObject;
          const index = _.findIndex(this.fir_sys_acc_dropdown_data_TEMP, { SYS_ACCT: this.rowData.SYSTEM_ACCT });
          if (index >= 0) {
            this.fir_sys_acct_selected = this.fir_sys_acc_dropdown_data_TEMP[index]?.SYS_DROPDOWN || 'Please select System Account';
            const sysAcctKey = this.fir_sys_acc_dropdown_data_TEMP[index]?.SYS_ACCT_KEY;
            if (sysAcctKey) this.fetchSystemAcctFormData(sysAcctKey);
          }
          this.loadingFirSysAcct = false;
        } else {
          this.gtwSnackbarService.openSnackBar('Unable to load system account data', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) return;
        console.error('Error loading system account dropdown:', error);
        this.loadingFirSysAcct = false;
        this.cdr.detectChanges();
      }
    );
  }

  private fetchSystemAcctFormData(sysAcctKey: any) {
    this.loadingIRSform = true;
    const formEpoch = ++this.formFetchEpoch;
    const params = {
      action_code: '4fmxwf',
      tax_year: this.rowData.TAX_YEAR,
      sys_acct: sysAcctKey
    };

    this.firSysFormService.loadJsonObject(this.GENERAL_CONFIG.base_url, params).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (formEpoch !== this.formFetchEpoch) return;
        if (data.callSuccess === '1') {
          this.firIRSFormData = data.jsonObject || [];
        }
        this.loadingIRSform = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (formEpoch !== this.formFetchEpoch) return;
        console.error('Error loading IRS form data:', error);
        this.loadingIRSform = false;
        this.cdr.detectChanges();
      }
    );
  }

  private fetchFirSystemAcctLookup(epoch: number) {
    this.loadingFirLookup = true;
    const params = {
      action_code: 'oz9yxt',
      tax_year: this.rowData.TAX_YEAR ?? this.globalService?.globalParams?.tax_year,
      scenario: this.rowData.SCENARIO ?? this.globalService?.globalParams?.scenario,
      jcd_key: this.rowData.JCD_KEY ?? this.globalService?.globalParams?.jcd_key
    };

    this.firSysFormService.loadJsonObject(this.GENERAL_CONFIG.base_url, params).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (data: any) => {
        if (epoch !== this.loadEpoch) return;
        if (data.callSuccess === '1') {
          this.firSystemAcctLookupTemp = data.jsonObject || [];
          this.firLookupcheckdata = data.jsonObject || [];
          this.firLookupForm = _.uniq((data.jsonObject || []).map((item: any) => item.FORM));
        }
        this.loadingFirLookup = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) return;
        console.error('Error loading lookup data:', error);
        this.loadingFirLookup = false;
        this.cdr.detectChanges();
      }
    );
  }

  firToggleLookup() {
    this.CHECK_FIR_FORM = '';
    this.CHECK_FIR_SCHEDULE = '';
    this.CHECK_FIR_LINE = '';
    this.CHECK_FIR_COL = '';
    this.CHECK_FIR_SYS_MAP_ACCT = '';
    this.firLookUpMapping = !this.firLookUpMapping;
    this.showFirDetailsText = 'lookup';
    this.cdr.detectChanges();
  }

  firFormAdd() {
    this.showFirDetailsText = 'add';
    this.firLookUpMapping = true;
    this.CHECK_FIR_FORM = '';
    this.CHECK_FIR_SCHEDULE = '';
    this.CHECK_FIR_LINE = '';
    this.CHECK_FIR_COL = '';
    this.CHECK_FIR_SYS_MAP_ACCT = this.fir_sys_acct_selected;
    this.cdr.detectChanges();
  }

  formEdit(item: any, index: number) {
    this.CHECK_FIR_SYS_MAP_ACCT = this.fir_sys_acct_selected;
    this.old_fir_form_acct_key = item.FORM_ACCT_KEY;
    this.old_fir_form_map_type = item.MAP_TYPE;
    this.showFirDetailsText = 'edit';
    this.currentEditedIndex = index;
    this.firLookUpMapping = true;

    this.CHECK_FIR_FORM = item.FORMS;
    this.CHECK_FIR_SCHEDULE = item.SCHEDULE;
    this.CHECK_FIR_LINE = item.LINE + ' - ' + item.LINE_DESC;
    this.CHECK_FIR_COL = item.COL;

    const b = item.FORMS.trim();
    const c = item.SCHEDULE.trim();
    const d = item.LINE;
    const testdata = this.firLookupcheckdata || [];

    this.firLookupSchedule = _.uniq(testdata.filter((o: any) => o.FORM === b).map((o: any) => o.SCHEDULE));
    this.firLookupLine = _.uniq(testdata.filter((o: any) => o.FORM === b && o.SCHEDULE === c).map((o: any) => o.LINE));
    this.firLookupColumn = _.uniq(testdata.filter((o: any) => o.FORM === b && o.SCHEDULE === c && o.LINE === d).map((o: any) => o.COL));

    this.cdr.detectChanges();
  }

  getFirLookupNext(key: number, nextLookupKey: string, currentLookupValue: string) {
    const data = _.cloneDeep(this.firLookupcheckdata);

    switch (key) {
      case 1: {
        const filtered = data.filter((o: any) => o.FORM === currentLookupValue);
        this.firLookupData1 = filtered;
        this.firLookupSchedule = _.uniq(filtered.map((o: any) => o[nextLookupKey]));
        break;
      }
      case 2: {
        const filtered = this.firLookupData1.filter((o: any) => o.SCHEDULE === currentLookupValue);
        this.firLookupData2 = filtered;
        this.firLookupLine = _.uniq(filtered.map((o: any) => o[nextLookupKey]));
        break;
      }
      case 3: {
        const filtered = this.firLookupData2.filter((o: any) => o.LINE === currentLookupValue);
        this.firLookupData3 = filtered;
        this.firLookupColumn = _.uniq(filtered.map((o: any) => o[nextLookupKey]));
        this.firLookupSysMapAcct = _.uniq(filtered.map((o: any) => o.SYS_ACCT));
        break;
      }
      case 4: {
        const filtered = this.firLookupData3.filter((o: any) => o.COL === currentLookupValue);
        this.firLookupSysMapAcct = _.uniq(filtered.map((o: any) => o[nextLookupKey]));
        break;
      }
    }
    this.cdr.detectChanges();
  }

  firFormchanged() {
    this.CHECK_FIR_SCHEDULE = '';
    this.CHECK_FIR_LINE = '';
    this.CHECK_FIR_COL = '';
    this.cdr.detectChanges();
  }

  firSchedulechanged() {
    this.CHECK_FIR_LINE = '';
    this.CHECK_FIR_COL = '';
    this.cdr.detectChanges();
  }

  firLinechanged() {
    this.CHECK_FIR_COL = '';
    this.cdr.detectChanges();
  }

  sortBy(key: string, value: number) {
    switch (value) {
      case 0: this.sort_key[key] = 1; this.reverseSort = false; break;
      case 1: this.sort_key[key] = 2; this.reverseSort = true; break;
      case 2: this.sort_key[key] = 0; this.reverseSort = false; break;
    }
    this.cdr.detectChanges();
  }

  getSortedData(): any[] {
    if (!this.firIRSFormData) return [];
    return _.orderBy(this.firIRSFormData, [this.orderByField], [this.reverseSort ? 'desc' : 'asc']);
  }

  save() {
    if (this.crudLoading) {
      this.gtwSnackbarService.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }
    const epoch = this.loadEpoch;
    this.crudLoading = true;
    this.cdr.detectChanges();

    const jsonSetting = {
      TAX_YEAR: this.rowData.TAX_YEAR,
      JCD_KEY: this.rowData.JCD_KEY ?? this.globalService?.globalParams?.jcd_key,
      SCENARIO: this.rowData.SCENARIO ?? this.globalService?.globalParams?.scenario
    };

    const jsonObj: any = {};
    jsonObj.SYSTEM_ACCT_KEY = this.rowData.SYS_ACCT_KEY;
    jsonObj.GROUP_OBJ_KEY = this.rowData.GROUP_OBJ_KEY;

    const index = _.findIndex(this.firSystemAcctLookupTemp, {
      FORM: this.CHECK_FIR_FORM.trim(),
      SCHEDULE: this.CHECK_FIR_SCHEDULE.trim(),
      LINE: this.CHECK_FIR_LINE.trim(),
      COL: this.CHECK_FIR_COL.trim()
    });

    if (this.showFirDetailsText === 'add') {
      jsonObj.NEW_FORM_ACCT_KEY = index >= 0 ? this.firSystemAcctLookupTemp[index].FORM_ACCT_KEY : null;
      jsonObj.OLD_FORM_ACCT_KEY = null;
      jsonObj.MAP_TYPE = 'SYSTEM(US)_TO_FORM_MAP';
    } else if (this.showFirDetailsText === 'edit') {
      jsonObj.OLD_FORM_ACCT_KEY = this.old_fir_form_acct_key ?? null;
      jsonObj.NEW_FORM_ACCT_KEY = index >= 0 ? this.firSystemAcctLookupTemp[index].FORM_ACCT_KEY : null;
      jsonObj.MAP_TYPE = this.old_fir_form_map_type;
    }

    const returnObj = [jsonObj];

    this.firSysFormService.saveFirIRSFormDetails(
      this.GENERAL_CONFIG.base_url, jsonSetting, returnObj, this.globalService, this.filterParams
    ).pipe(takeUntil(this.unsubscribe$)).subscribe(
      (result: any) => {
        if (epoch !== this.loadEpoch) {
          this.crudLoading = false;
          this.cdr.detectChanges();
          return;
        }
        this.crudLoading = false;
        if (result.errorMessage && result.errorMessage !== 'null') {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        } else {
          this.gtwSnackbarService.openSnackBar('IRS Form has been successfully saved/updated', 'gtw-snackbar--green');
          const sysAcctKey = this.rowData.SYS_ACCT_KEY;
          if (sysAcctKey) this.fetchSystemAcctFormData(sysAcctKey);
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        if (epoch !== this.loadEpoch) {
          this.crudLoading = false;
          this.cdr.detectChanges();
          return;
        }
        this.crudLoading = false;
        this.gtwSnackbarService.openSnackBar('Error occurred while saving. Please try again.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      }
    );
  }

  reset() {
    this.firLookUpMapping = false;
    this.showFirDetailsText = 'lookup';
    this.CHECK_FIR_FORM = '';
    this.CHECK_FIR_SCHEDULE = '';
    this.CHECK_FIR_LINE = '';
    this.CHECK_FIR_COL = '';
    this.CHECK_FIR_SYS_MAP_ACCT = '';
    this.cdr.detectChanges();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }
}
